import { parseCoasterProjectV1, type CoasterProjectV1 } from './project';

export class CanonicalSerializationError extends Error {
  readonly code = 'canonical_serialization_failed';

  constructor(message: string) {
    super(message);
    this.name = 'CanonicalSerializationError';
  }
}

export function canonicalStringify(value: unknown): string {
  const active = new WeakSet<object>();

  const visit = (current: unknown, path: string): string => {
    if (current === null) return 'null';
    if (typeof current === 'string' || typeof current === 'boolean') return JSON.stringify(current);
    if (typeof current === 'number') {
      if (!Number.isFinite(current))
        throw new CanonicalSerializationError(`Non-finite number at ${path}`);
      return Object.is(current, -0) ? '0' : JSON.stringify(current);
    }
    if (typeof current !== 'object')
      throw new CanonicalSerializationError(`Unsupported ${typeof current} at ${path}`);
    if (active.has(current)) throw new CanonicalSerializationError(`Circular reference at ${path}`);

    active.add(current);
    try {
      if (Array.isArray(current)) {
        const values: string[] = [];
        for (let index = 0; index < current.length; index += 1) {
          if (!Object.prototype.hasOwnProperty.call(current, index))
            throw new CanonicalSerializationError(`Sparse array at ${path}[${index}]`);
          values.push(visit(current[index], `${path}[${index}]`));
        }
        return `[${values.join(',')}]`;
      }

      const prototype = Object.getPrototypeOf(current);
      if (prototype !== Object.prototype && prototype !== null)
        throw new CanonicalSerializationError(`Non-plain object at ${path}`);
      const record = current as Record<string, unknown>;
      return `{${Object.keys(record)
        .sort()
        .map((key) => `${JSON.stringify(key)}:${visit(record[key], `${path}.${key}`)}`)
        .join(',')}}`;
    } finally {
      active.delete(current);
    }
  };

  return visit(value, '$');
}

export function serializeCoasterProject(project: CoasterProjectV1): string {
  return canonicalStringify(parseCoasterProjectV1(project));
}

export async function sha256Canonical(value: unknown): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new CanonicalSerializationError('Web Crypto SHA-256 is unavailable');
  const digest = await subtle.digest(
    'SHA-256',
    new TextEncoder().encode(canonicalStringify(value)),
  );
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function hashCoasterProject(project: CoasterProjectV1): Promise<string> {
  return sha256Canonical(parseCoasterProjectV1(project));
}
